import { useContext } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import CreateEditClientAccountHeader from "./CreateEditClientAccountHeader";
import CreateEditClientAccountForm from "./CreateEditClientAccountForm";
import { LayoutContext } from "../../../context/LayoutContext";
import {
  createClientAccount,
  getClientAccountById,
  updateClientAccount,
} from "../../../services/api/client";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const validationSchema = Yup.object({
  clientId: Yup.number().required("Client is required"),
  accountsPerson: Yup.string().required("Accounts Person is required"),
  email: Yup.string().required("Email is required").email("Invalid email format"),
  phone: Yup.string().notRequired(),
  paymentTerms: Yup.string().notRequired(),
  taxType: Yup.string().notRequired(),
  taxPercentage: Yup.number()
    .notRequired()
    .min(0, "Tax cannot be negative")
    .max(100, "Tax cannot exceed 100"),
});

const CreateEditClientAccount = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: accountData, isLoading: isFetching } = useQuery({
    queryKey: ["clientAccount", id],
    queryFn: () => getClientAccountById(id),
    enabled: !!id,
  });

  const { mutateAsync: createClientAccountMutation, isLoading: isCreating } = useMutation({
    mutationFn: createClientAccount,
  });
  const { mutateAsync: updateClientAccountMutation, isLoading: isUpdating } = useMutation({
    mutationFn: updateClientAccount,
  });

  const formik = useFormik({
    initialValues: {
      clientId: accountData?.data?.clientId || "",
      accountsPerson: accountData?.data?.accountsPerson || "",
      email: accountData?.data?.email || "",
      phone: accountData?.data?.phone || "",
      paymentTerms: accountData?.data?.paymentTerms || null,
      taxType: accountData?.data?.taxType || null,
      taxPercentage: accountData?.data?.taxPercentage || null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleClientAccountCreateOrUpdate(values);
    },
  });
  const handleClientAccountCreateOrUpdate = async (values) => {
    try {
      const payload = {
        clientId: values.clientId,
        accountsPerson: values.accountsPerson,
        email: values.email,
        phone: values.phone,
        paymentTerms: values.paymentTerms,
        taxType: values.taxType,
        taxPercentage: values.taxPercentage,
      };

      if (isEdit) {
        await updateClientAccountMutation({ id, payload });
      } else {
        await createClientAccountMutation(payload);
      }

      navigate("/workBoard/clients/account");
    } catch (error) {
      console.log("error", error);
      dispatch(showToast({ message: error.response.data.message, type: "error" }));
    }
  };

  if (isEdit && isCreating) return <div>Loading...</div>;

  return (
    <div
      className={`bg-[#F6F6F6] h-full relative ${isBannerVisible ? "top-[107px]" : "top-[56px]"}`}
    >
      <CreateEditClientAccountHeader
        onSave={formik.handleSubmit}
        isUpdated={formik.dirty}
        isLoading={isCreating}
      />
      <CreateEditClientAccountForm formik={formik} />
    </div>
  );
};

export default CreateEditClientAccount;
